import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import { PlusIcon, Loader2, CheckIcon, XIcon, LogOutIcon, CircleDashed } from 'lucide-react';
import api from 'config/axiosConfig';
import { toast } from 'sonner';
import DialogConfirmation from 'ui/DialogConfirmation';
import RequestRespondModal from './RequestRespondModal';

interface Props {
    teamId: number;
    teamName: string;
    isPrivate?: boolean;
    className?: string;
    onJoinChange?: (isJoined: boolean) => void;
}

type JoinStatus = 'not_joined' | 'joined' | 'requested' | 'invited' | 'loading' | 'creator';

const JoinButton: React.FC<Props> = ({ 
    teamId, 
    teamName,
    isPrivate = false,
    className = '',
    onJoinChange
}) => {
    const { currentUser, setShowAuthModal, awaitingUser } = useUser();
    const [joinStatus, setJoinStatus] = useState<JoinStatus>('loading');
    const [isHovered, setIsHovered] = useState(false);
    const [requestId, setRequestId] = useState<number | null>(null);
    const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
    const [showRequestModal, setShowRequestModal] = useState(false);

    // Check initial join status
    useEffect(() => {
        const checkJoinStatus = async () => {
            // If still loading user, don't do anything yet
            if (awaitingUser) {
                return;
            }

            // If no user, set to not joined
            if (!currentUser) {
                setJoinStatus('not_joined');
                return;
            }

            try {
                const response = await api.get(`/api/team/${teamId}/members`);
                const members = response.data;
                const userMembership = members.find((member: any) => 
                    member.member_id === currentUser.id && 
                    member.member_type === 'user' &&
                    member.properties?.is_active === true
                );

                if (userMembership) {
                    //console.log('userMembership', userMembership);
                    if (userMembership.properties.is_creator === true) {
                        setJoinStatus('creator');
                    } else {
                        setJoinStatus('joined');
                    }
                } else {
                    // Check for pending request
                    const joinRequestResponse = await api.get(`/api/requests`, {
                        params: {
                            requestorId: currentUser.id,
                            requestorType: 'user',
                            requesteeId: teamId,
                            requesteeType: 'team',
                            requestType: 'team_join_request'
                        }
                    })
                    const inviteRequestResponse = await api.get(`/api/requests`, {
                        params: {
                            entityId: teamId,
                            entityType: 'team',
                            requesteeId: currentUser.id,
                            requesteeType: 'user',
                            requestType: 'team_invite'
                        }
                    })
                    const hasJoinRequest = joinRequestResponse.data.requests.length > 0;
                    const hasInviteRequest = inviteRequestResponse.data.requests.length > 0;
                    if (hasJoinRequest) {
                        setRequestId(joinRequestResponse.data.requests[0].id);
                        setJoinStatus('requested');
                    } else if (hasInviteRequest) {
                        setRequestId(inviteRequestResponse.data.requests[0].id);
                        setJoinStatus('invited');
                    } else {
                        setJoinStatus('not_joined');
                    }
                }
            } catch (error) {
                console.error('Error checking join status:', error);
                setJoinStatus('not_joined');
            }
        };

        checkJoinStatus();
    }, [currentUser, teamId, awaitingUser]);

    const handleClick = async () => {
        if (!currentUser) {
            setShowAuthModal(true);
            return;
        }

        if (joinStatus === 'loading') return;

        if (joinStatus === 'invited') {
            setShowRequestModal(true);
            return;
        }

        if (joinStatus === 'joined') {
            setShowLeaveConfirmation(true);
            return;
        }

        const prevStatus = joinStatus;
        setJoinStatus('loading');

        try {
            if (joinStatus === 'requested' && requestId) {
                // Cancel the request
                await api.delete(`/api/requests/${requestId}/cancel`);
                setJoinStatus('not_joined');
                setRequestId(null);
                onJoinChange?.(false);
                toast.success(`Cancelled join request to ${teamName}`);
            } else {
                // Send join request
                await api.post(`/api/team/${teamId}/join`);
                setJoinStatus('requested');
                onJoinChange?.(false);
                toast.success(`Sent join request to ${teamName}`);
            }
        } catch (error) {
            console.error('Error handling join request:', error);
            setJoinStatus(prevStatus);
            toast.error(joinStatus === 'requested' ? 'Failed to cancel join request' : 'Failed to send join request');
        }
    };

    const handleLeave = async () => {
        const prevStatus = joinStatus;
        if (joinStatus === 'creator') {
            return;
        }
        setJoinStatus('loading');
        setShowLeaveConfirmation(false);

        try {
            await api.delete(`/api/team/${teamId}/members`, {
                data: {
                    memberId: currentUser?.id,
                    memberType: 'user'
                }
            });
            setJoinStatus('not_joined');
            onJoinChange?.(false);
            toast.success(`Left ${teamName}`);
        } catch (error) {
            console.error('Error leaving team:', error);
            setJoinStatus(prevStatus);
            toast.error('Failed to leave team');
        }
    };

    const getButtonContent = () => {
        if (joinStatus === 'loading') {
            return <Loader2 className="size-4 animate-spin" />;
        }

        if (joinStatus === 'creator') {
            return null;
        }

        if (joinStatus === 'joined') {
            return (
                <>
                    {isHovered ? (
                        <>
                            <LogOutIcon className="size-4" />
                            <span>Leave&nbsp;</span>
                        </>
                    ) : (
                        <>
                            <CheckIcon className="size-4" />
                            <span>Joined&nbsp;</span>
                        </>
                    )}
                </>
            );
        }

        if (joinStatus === 'requested' || joinStatus === 'invited') {
            return (
                <>
                    {isHovered ? (
                        <>
                            <XIcon className="size-4" />
                            <span>{joinStatus === 'requested' ? 'Unrequest' : 'Respond'}&nbsp;</span>
                        </>
                    ) : (
                        <>
                            <CircleDashed className="size-4" />
                            <span>{joinStatus === 'requested' ? 'Requested' : 'Invited'}&nbsp;</span>
                        </>
                    )}
                </>
            );
        }

        return (
            <>
                <PlusIcon className="size-4" />
                <span>Join&nbsp;</span>
            </>
        );
    };

    // Don't render anything while waiting for user state
    if (awaitingUser) {
        return null;
    }

    // Don't render if no user and not awaiting
    if (!currentUser?.id) {
        return null;
    }

    return (
        <>
            <button
                onClick={handleClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                disabled={joinStatus === 'loading' || joinStatus === 'creator'}
                className={`flex flex-row shadow-sm items-center bg-transparent border-2 px-2 py-1 font-semibold 
                text-eoe-text dark:text-eoe-dark-text border-eoe-text dark:border-eoe-dark-text 
                hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover 
                disabled:opacity-50 disabled:cursor-not-allowed
                rounded-full text-sm w-fit gap-1
                ${joinStatus === 'requested' && isHovered ? 'hover:bg-eoe-button-secondary-hover dark:hover:bg-eoe-dark-button-secondary-hover' : ''}
                ${joinStatus === 'invited' && isHovered ? 'hover:bg-eoe-button-primary-hover dark:hover:bg-eoe-dark-button-primary-hover' : ''}
                ${joinStatus === 'joined' && isHovered ? 'hover:bg-red-100 dark:hover:bg-red-900' : ''}
                ${joinStatus === 'creator' ? 'hidden' : ''}
                ${className}`}
            >
                {getButtonContent()}
            </button>
            {joinStatus === 'joined' && showLeaveConfirmation && (
            <DialogConfirmation
                isOpen={showLeaveConfirmation}
                onCancel={() => setShowLeaveConfirmation(false)}
                onConfirm={handleLeave}
                title="Leave Partea"
                description={`Are you sure you want to leave ${teamName}? You will need to request to join again.`}
                confirmText="Leave"
                cancelText="Cancel"
            />
            )}

            {/* Request Respond Modal */}
            {showRequestModal && requestId && (
                <RequestRespondModal 
                    onClose={() => {
                        setShowRequestModal(false);
                        // Refresh status after modal closes
                        if (currentUser) {
                            const refreshStatus = async () => {
                                try {
                                    // First check if user is team creator
                                    const creatorResponse = await api.get(`/api/team/${teamId}`);
                                    if (creatorResponse.data.properties_public?.creator_id === currentUser.id) {
                                        setJoinStatus('creator');
                                        return;
                                    }
                                    
                                    // Next check if user is a member
                                    const memberResponse = await api.get(`/api/team/${teamId}/members`, {
                                        params: {
                                            member_id: currentUser.id,
                                            member_type: 'user'
                                        }
                                    });
                                    
                                    if (memberResponse.data && memberResponse.data.length > 0) {
                                        setJoinStatus('joined');
                                        onJoinChange?.(true);
                                        return;
                                    }
                                    
                                    setJoinStatus('not_joined');
                                } catch (error) {
                                    console.error('Error refreshing join status:', error);
                                }
                            };
                            refreshStatus();
                        }
                    }}
                    refRequestId={requestId}
                />
            )}
        </>
    );
};

export default JoinButton; 